/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	Injectable
} from '@angular/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	FileHelper
} from '@shared/helpers/file.helper';
import {
	IFileUploadSettings
} from '@shared/interfaces/application-objects/file-upload-settings';
import {
	IFileUploadTypeConfiguration
} from '@shared/interfaces/application-objects/file-upload-type-configuration';
import {
	SessionService
} from '@shared/services/session.service';

/**
 * A service for file type operations using configuration-driven validation.
 *
 * @export
 * @class FileTypeService
 */
@Injectable({
	providedIn: 'root'
})
export class FileTypeService
{
	/**
	 * Initializes a new instance of FileTypeService.
	 *
	 * @param {SessionService} sessionService
	 * The session service for accessing file upload settings.
	 * @memberof FileTypeService
	 */
	public constructor(
		private readonly sessionService: SessionService)
	{
		this.fileUploadSettings =
			this.sessionService.systemSettings.fileUploads;
	}

	/**
	 * File upload settings.
	 *
	 * @private
	 * @type {IFileUploadSettings}
	 * @memberof FileTypeService
	 */
	private readonly fileUploadSettings: IFileUploadSettings;

	/**
	 * Finds the extension in the string passed if it is known.
	 *
	 * @param {string} filename
	 * The file name or string to search.
	 * @returns {string}
	 * The extension or null if not found.
	 * @memberof FileTypeService
	 */
	public findKnownExtension(
		filename: string): string
	{
		if (AnyHelper.isNullOrWhitespace(filename))
		{
			return null;
		}

		const supportedTypes: IFileUploadTypeConfiguration[] =
			this.fileUploadSettings.supportedFileTypes;

		if (AnyHelper.isNullOrEmpty(supportedTypes))
		{
			return null;
		}

		const fileNameWithoutQueryString: string =
			filename.split(AppConstants.characters.questionMark)[0];
		const segments: string[] =
			fileNameWithoutQueryString.split(
				AppConstants.characters.period);

		let foundExtension: string = null;

		segments.forEach(
			(segment: string) =>
			{
				const isKnownType: boolean =
					supportedTypes.some(
						(type: IFileUploadTypeConfiguration) =>
							type.fileExtension === segment.toLowerCase());

				if (isKnownType)
				{
					foundExtension = segment;
				}
			});

		return foundExtension;
	}

	/**
	 * Gets a comma-separated list of accepted file extensions
	 * for file input controls.
	 *
	 * @returns {string}
	 * A comma-separated string of file extensions
	 * (e.g., ".pdf,.jpg,.txt") or empty string if none configured.
	 * @memberof FileTypeService
	 */
	public getAcceptedFileExtensions(): string
	{
		const supportedTypes: IFileUploadTypeConfiguration[] =
			this.fileUploadSettings.supportedFileTypes;

		if (AnyHelper.isNullOrEmpty(supportedTypes))
		{
			return AppConstants.empty;
		}

		return supportedTypes
			.map((type: IFileUploadTypeConfiguration) =>
				`.${type.fileExtension}`)
			.join(AppConstants.characters.comma);
	}

	/**
	 * Gets the maximum file size in bytes for a given file extension.
	 *
	 * @param {string} extension
	 * The file extension (without dot, e.g., 'pdf', 'zip').
	 * @returns {number}
	 * The maximum file size in bytes, or default if not configured.
	 * @memberof FileTypeService
	 */
	public getMaxFileSizeInBytes(
		extension: string): number
	{
		const settings: IFileUploadSettings =
			this.fileUploadSettings;

		if (AnyHelper.isNull(settings) ||
			AnyHelper.isNull(settings.supportedFileTypes))
		{
			return 50 * FileHelper.oneMegabyte;
		}

		if (AnyHelper.isNullOrEmpty(extension))
		{
			return settings.maxFileSize * FileHelper.oneMegabyte;
		}

		const fileType: IFileUploadTypeConfiguration =
			settings.supportedFileTypes
				.find((type: IFileUploadTypeConfiguration) =>
					type.fileExtension.toLowerCase() ===
						extension.toLowerCase());

		if (AnyHelper.isNull(fileType))
		{
			return settings.maxFileSize * FileHelper.oneMegabyte;
		}

		const maxSizeInMB: number =
			fileType.maxFileSize ?? settings.maxFileSize;

		return maxSizeInMB * FileHelper.oneMegabyte;
	}

	/**
	 * Gets the invalid file type message detail from system settings.
	 *
	 * @returns {string}
	 * The configured message detail.
	 * @memberof FileTypeService
	 */
	public getInvalidFileTypeMessageDetail(): string
	{
		return this.fileUploadSettings.invalidFileTypeMessageDetail;
	}

	/**
	 * Gets the invalid file size message detail from system settings.
	 *
	 * @returns {string}
	 * The configured message detail.
	 * @memberof FileTypeService
	 */
	public getInvalidFileSizeMessageDetail(): string
	{
		return this.fileUploadSettings.invalidFileSizeMessageDetail;
	}
}